import React from 'react';
import { FilterChip } from './FilterChip';
import { FilterAddButton } from './FilterAddButton';
import { FilterValueEditor } from './FilterValueEditor';
// ---------------------------------------------------------------------------
// Public component — dispatches by mode
// ---------------------------------------------------------------------------
/**
 * Renders the hybrid filter bar pattern used across filtered table pages:
 *
 *   [active chips first]  [inactive chips (dashed)]  [+ Add Filter]
 *
 * Supports two modes:
 *
 * **Callback mode** — page owns chipConfigs + render switches + getValue.
 * Used when integrating into an existing class component that can't adopt
 * the declarative config API. Pass `filters + active + chipConfigs +
 * getValue + isActive + renderEditor + renderDropdown + onToggle`.
 *
 * **Config mode** — page provides a single `FilterConfig[]` + values +
 * displayed + onChange + onToggle. Editors are derived from config.render
 * (presets) or FilterValueEditor by kind. Pass `configs + values + labels
 * + displayed + onChange + onLabelChange + onToggle`.
 */
export function FilterBarRegistry(props) {
    if ('configs' in props && props.configs != null) {
        return React.createElement(ConfigMode, Object.assign({}, props));
    }
    return React.createElement(CallbackMode, Object.assign({}, props));
}
// ---------------------------------------------------------------------------
// Callback mode impl (original behavior)
// ---------------------------------------------------------------------------
function CallbackMode({ filters, active, chipConfigs, getValue, isActive, renderEditor, renderDropdown, onToggle, visibleWhen, }) {
    const isVisible = (key) => (visibleWhen ? visibleWhen(key) : true);
    const visibleActive = active.filter(isVisible);
    const availableForAdd = filters.filter((f) => isVisible(f.key));
    const activeChips = visibleActive.filter(isActive);
    const inactiveDropdowns = visibleActive.filter((key) => !isActive(key));
    return (React.createElement(React.Fragment, null,
        activeChips.map((key) => {
            const cfg = chipConfigs[key];
            if (!cfg)
                return null;
            return (React.createElement(FilterChip, { key: key, config: cfg, value: getValue(key), onRemove: () => onToggle(key) }, renderEditor(key)));
        }),
        inactiveDropdowns.map((key) => (React.createElement("div", { key: key, className: "shrink-0" }, renderDropdown(key)))),
        React.createElement(FilterAddButton, { available: availableForAdd, active: visibleActive, onToggle: onToggle })));
}
// ---------------------------------------------------------------------------
// Config mode impl (declarative)
// ---------------------------------------------------------------------------
function ConfigMode({ configs, values, labels, displayed, onChange, onLabelChange, onToggle, }) {
    const visibleConfigs = configs.filter((c) => !c.featureFlag || c.featureFlag());
    const visibleDisplayed = displayed.filter((id) => visibleConfigs.some((c) => c.id === id));
    const ordered = orderActiveFirst(visibleConfigs, values, visibleDisplayed);
    return (React.createElement(React.Fragment, null,
        ordered.map((cfg) => {
            const value = values[cfg.id];
            const filterLabels = (labels === null || labels === void 0 ? void 0 : labels[cfg.id]) || {};
            // Build render context for custom renderers (presets/autocompletes)
            const ctx = {
                value,
                labels: filterLabels,
                onChange: (v) => onChange(cfg.id, v),
                onAdd: (id, label) => {
                    const current = Array.isArray(value) ? value : [];
                    if (!current.includes(id)) {
                        onChange(cfg.id, [...current, id]);
                    }
                    onLabelChange === null || onLabelChange === void 0 ? void 0 : onLabelChange(cfg.id, id, label);
                },
                onRemove: (id) => {
                    const current = Array.isArray(value) ? value : [];
                    onChange(cfg.id, current.filter((v) => v !== id));
                },
                onClear: () => {
                    const empty = cfg.kind === 'select-multi' || cfg.kind === 'autocomplete-multi'
                        ? []
                        : cfg.kind === 'toggle'
                            ? false
                            : null;
                    onChange(cfg.id, empty);
                },
            };
            const editor = cfg.render ? (cfg.render(ctx)) : (React.createElement(FilterValueEditor, { config: cfg, value: value, onChange: (v) => onChange(cfg.id, v) }));
            // Chip summary for autocomplete kinds pulls from the label cache
            const chipValue = getChipValue(cfg, value, filterLabels);
            return (React.createElement(FilterChip, { key: cfg.id, config: cfg, value: chipValue, onRemove: () => onToggle(cfg.id), onClear: ctx.onClear }, editor));
        }),
        React.createElement(FilterAddButton, { available: visibleConfigs.map((c) => ({ key: c.id, label: c.label })), active: visibleDisplayed, onToggle: onToggle })));
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isValueActive(v, kind) {
    if (v == null)
        return false;
    if (v === '')
        return false;
    if (v === false)
        return false;
    if (Array.isArray(v) && v.length === 0)
        return false;
    if (kind === 'date-range') {
        const r = v;
        return r.from != null && r.from !== '' && r.to != null && r.to !== '';
    }
    return true;
}
function orderActiveFirst(configs, values, displayed) {
    const byId = new Map(configs.map((c) => [c.id, c]));
    const displayedCfgs = displayed
        .map((id) => byId.get(id))
        .filter((c) => c != null);
    const active = displayedCfgs.filter((c) => isValueActive(values[c.id], c.kind));
    const inactive = displayedCfgs.filter((c) => !isValueActive(values[c.id], c.kind));
    return [...active, ...inactive];
}
/**
 * For chip value summary: autocomplete kinds get their value replaced with
 * a human-readable string from the label cache (first label + "+N" for
 * multi-select). FilterChip's getValueSummary falls into its "text" branch
 * when we return a string.
 */
function getChipValue(cfg, value, labels) {
    if (cfg.kind === 'autocomplete-single') {
        const id = value;
        return id ? labels[id] || id : null;
    }
    if (cfg.kind === 'autocomplete-multi') {
        const ids = value || [];
        if (ids.length === 0)
            return [];
        const first = labels[ids[0]] || ids[0];
        return ids.length > 2
            ? `${first}, ${labels[ids[1]] || ids[1]} (+${ids.length - 2})`
            : ids.map((id) => labels[id] || id).join(', ');
    }
    return value;
}
